import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  CrossPromotionCampaign,
  CreateCrossPromotionCampaignDTO,
  UploadCrossPromotionImageResponse,
  UpdateCrossPromotionCampaignDTO,
} from "../types/crossPromotion";
import { auth } from "../firebase/config";

const CROSS_PROMOTION_COLLECTION = "crossPromotionCampaigns";
const UPLOAD_CROSS_PROMOTION_IMAGE_ENDPOINT =
  process.env.REACT_APP_UPLOAD_CROSS_PROMOTION_IMAGE_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/uploadCrossPromotionCampaignImage";

class CrossPromotionService {
  private convertTimestampToDate(value: any): Date {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    if (value?.toDate && typeof value.toDate === "function") {
      return value.toDate();
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  private sanitizeLocaleMap(value: any): CrossPromotionCampaign["localizedContent"] {
    if (!value || typeof value !== "object") {
      return {};
    }

    const entries = Object.entries(value)
      .map(([locale, payload]) => {
        const safeLocale = String(locale || "").trim();
        if (!safeLocale || !payload || typeof payload !== "object") return null;

        const item = payload as Record<string, unknown>;
        return [
          safeLocale,
          {
            headline: String(item.headline || "").trim(),
            subheading: String(item.subheading || "").trim(),
            cta: String(item.cta || "").trim(),
          },
        ] as const;
      })
      .filter((item): item is readonly [string, CrossPromotionCampaign["localizedContent"][string]] =>
        Boolean(item),
      );

    return Object.fromEntries(entries);
  }

  private mapDoc(data: any): CrossPromotionCampaign {
    const safeAppId = String(data.appId || "").trim();
    const safeAppName = String(data.appName || "").trim();
    const rawTargeting = data.targeting && typeof data.targeting === "object" ? data.targeting : null;
    const normalizedTargetingType =
      rawTargeting?.type === "all_apps" || rawTargeting?.type === "specific_app"
        ? rawTargeting.type
        : safeAppId
          ? "specific_app"
          : "all_apps";

    const targeting =
      normalizedTargetingType === "specific_app"
        ? {
            type: "specific_app" as const,
            appId: String(rawTargeting?.appId || safeAppId).trim(),
            appName: String(rawTargeting?.appName || safeAppName).trim(),
          }
        : {
            type: "all_apps" as const,
          };

    return {
      campaignId: String(data.campaignId || ""),
      name: String(data.name || "").trim(),
      targeting,
      appId: targeting.type === "specific_app" ? targeting.appId : undefined,
      appName: targeting.type === "specific_app" ? targeting.appName : undefined,
      campaignBrief: String(data.campaignBrief || "").trim() || undefined,
      status: data.status || "draft",
      defaultLocale: String(data.defaultLocale || "en-US").trim(),
      localizedContent: this.sanitizeLocaleMap(data.localizedContent),
      adImageUrl: String(data.adImageUrl || "").trim(),
      campaignDesignStyle: data.campaignDesignStyle || "screenshot_classic",
      storeLinks: {
        ios: String(data.storeLinks?.ios || "").trim(),
        android: String(data.storeLinks?.android || "").trim(),
      },
      createdAt: this.convertTimestampToDate(data.createdAt),
      updatedAt: this.convertTimestampToDate(data.updatedAt),
      createdBy: String(data.createdBy || "").trim(),
      updatedBy: String(data.updatedBy || "").trim(),
      activatedAt: data.activatedAt
        ? this.convertTimestampToDate(data.activatedAt)
        : undefined,
    };
  }

  private generateId(): string {
    return `crosspromo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      throw new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      );
    }

    return responseData as T;
  }

  private async fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Failed to read selected image."));
      reader.readAsDataURL(file);
    });
  }

  async listCampaigns(): Promise<CrossPromotionCampaign[]> {
    const snapshot = await getDocs(collection(db, CROSS_PROMOTION_COLLECTION));
    return snapshot.docs
      .map((item) => this.mapDoc(item.data()))
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }

  async createCampaign(
    userId: string,
    dto: CreateCrossPromotionCampaignDTO,
  ): Promise<CrossPromotionCampaign> {
    const campaignId = this.generateId();
    const payload = {
      campaignId,
      name: dto.name,
      targeting: dto.targeting,
      appId: dto.targeting.type === "specific_app" ? dto.targeting.appId || "" : "",
      appName:
        dto.targeting.type === "specific_app" ? dto.targeting.appName || "" : "",
      campaignBrief: dto.campaignBrief || "",
      status: dto.status || "draft",
      defaultLocale: dto.defaultLocale,
      localizedContent: dto.localizedContent,
      adImageUrl: dto.adImageUrl,
      campaignDesignStyle: dto.campaignDesignStyle,
      storeLinks: {
        ios: dto.storeLinks.ios,
        android: dto.storeLinks.android,
      },
      createdBy: userId,
      updatedBy: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(dto.status === "active" ? { activatedAt: serverTimestamp() } : {}),
    };

    await setDoc(doc(db, CROSS_PROMOTION_COLLECTION, campaignId), payload);

    if (dto.status === "active") {
      await this.activateCampaign(campaignId, userId);
    }

    return {
      ...this.mapDoc(payload),
      createdAt: new Date(),
      updatedAt: new Date(),
      activatedAt: dto.status === "active" ? new Date() : undefined,
    };
  }

  async updateCampaign(
    campaignId: string,
    userId: string,
    updates: UpdateCrossPromotionCampaignDTO,
  ): Promise<void> {
    const docRef = doc(db, CROSS_PROMOTION_COLLECTION, campaignId);

    const updatePayload: Record<string, any> = {
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    };

    if (updates.name !== undefined) updatePayload.name = updates.name;
    if (updates.targeting !== undefined) {
      updatePayload.targeting = updates.targeting;
      updatePayload.appId =
        updates.targeting.type === "specific_app" ? updates.targeting.appId || "" : "";
      updatePayload.appName =
        updates.targeting.type === "specific_app" ? updates.targeting.appName || "" : "";
    }
    if (updates.appId !== undefined) updatePayload.appId = updates.appId;
    if (updates.appName !== undefined) updatePayload.appName = updates.appName;
    if (updates.campaignBrief !== undefined) {
      updatePayload.campaignBrief = updates.campaignBrief;
    }
    if (updates.defaultLocale !== undefined) {
      updatePayload.defaultLocale = updates.defaultLocale;
    }
    if (updates.localizedContent !== undefined) {
      updatePayload.localizedContent = updates.localizedContent;
    }
    if (updates.adImageUrl !== undefined) {
      updatePayload.adImageUrl = updates.adImageUrl;
    }
    if (updates.campaignDesignStyle !== undefined) {
      updatePayload.campaignDesignStyle = updates.campaignDesignStyle;
    }
    if (updates.storeLinks !== undefined) {
      updatePayload.storeLinks = {
        ...(updates.storeLinks.ios !== undefined
          ? { ios: updates.storeLinks.ios }
          : {}),
        ...(updates.storeLinks.android !== undefined
          ? { android: updates.storeLinks.android }
          : {}),
      };
    }
    if (updates.status !== undefined) {
      updatePayload.status = updates.status;
      if (updates.status === "active") {
        updatePayload.activatedAt = serverTimestamp();
      }
    }

    await updateDoc(docRef, updatePayload);

    if (updates.status === "active") {
      await this.activateCampaign(campaignId, userId);
    }
  }

  async deleteCampaign(campaignId: string): Promise<void> {
    await deleteDoc(doc(db, CROSS_PROMOTION_COLLECTION, campaignId));
  }

  async activateCampaign(campaignId: string, userId: string): Promise<void> {
    const allSnapshot = await getDocs(collection(db, CROSS_PROMOTION_COLLECTION));
    const selectedDoc = allSnapshot.docs.find((item) => item.id === campaignId);
    if (!selectedDoc) {
      throw new Error("Campaign not found.");
    }

    const selectedCampaign = this.mapDoc(selectedDoc.data());
    const activeDocs = allSnapshot.docs.filter(
      (item) => item.data()?.status === "active",
    );
    const batch = writeBatch(db);

    activeDocs.forEach((item) => {
      if (item.id === campaignId) return;
      const activeCampaign = this.mapDoc(item.data());
      const hasConflict =
        selectedCampaign.targeting.type === "all_apps"
          ? activeCampaign.targeting.type === "all_apps"
          : activeCampaign.targeting.type === "specific_app" &&
            String(activeCampaign.targeting.appId || "").toLowerCase() ===
              String(selectedCampaign.targeting.appId || "").toLowerCase();
      if (!hasConflict) return;

      batch.update(item.ref, {
        status: "archived",
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      });
    });

    const selectedRef = doc(db, CROSS_PROMOTION_COLLECTION, campaignId);
    batch.update(selectedRef, {
      status: "active",
      activatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });

    await batch.commit();
  }

  async deactivateCampaign(campaignId: string, userId: string): Promise<void> {
    await updateDoc(doc(db, CROSS_PROMOTION_COLLECTION, campaignId), {
      status: "archived",
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    });
  }

  async uploadCampaignImage(
    file: File,
    campaignId?: string,
  ): Promise<UploadCrossPromotionImageResponse> {
    if (!file) {
      throw new Error("Image file is required.");
    }
    const dataUrl = await this.fileToDataUrl(file);
    return this.postAuthenticatedJson<UploadCrossPromotionImageResponse>(
      UPLOAD_CROSS_PROMOTION_IMAGE_ENDPOINT,
      {
        campaignId: campaignId || null,
        fileName: file.name || "campaign-image",
        dataUrl,
      },
    );
  }
}

const crossPromotionService = new CrossPromotionService();

export default crossPromotionService;
